import qs from 'qs';
import auth from '~/auth';

import { actions, definitions } from '@luxuryescapes/contract-svc-flights';

import { ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER } from '~/consts/roles';

import ApiError from '~/types/ApiError';

import { request } from './common';

function basePath() {
  return window.configs.API_HOST + '/api/';
}

// Match svc-flights requiresAdmin; other portal roles retain the legacy published-read path.
const FLIGHTS_ADMIN_ROLES = [ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, 'service'];
const draftPreviewSuffix = () =>
  auth.getState().user?.roles?.some((role) => FLIGHTS_ADMIN_ROLES.includes(role)) ? '?includeUnpublished=true' : '';

const validRates = ['daily', 'twice daily'];
const validTravelDayLongNames = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const validTravelDayNames = [
  ...validTravelDayLongNames,
  ...validTravelDayLongNames.map((name) => name.substring(0, 3)),
];

export const structureTravelDaysForApi = (travelDays: string) => {
  const days = travelDays
    .split(/\s*,\s*/)
    .map((value) => value.toLowerCase().trim())
    .filter(Boolean);

  if (days.length === 0) {
    return null;
  }

  if (days.length === 1 && validRates.includes(days[0])) {
    return days[0];
  }

  return days;
};

export const validateTravelDays = (travelDays: string | Array<string> | null) => {
  if (!travelDays) {
    return true;
  }
  if (typeof travelDays === 'string') {
    return validRates.includes(travelDays);
  }
  if (!Array.isArray(travelDays)) {
    return false;
  }
  return travelDays.every((day) => validTravelDayNames.includes(day));
};

export const transformTravelDaysFromApi = (travelDays: string | Array<string> | null) => {
  if (!travelDays) {
    return '';
  }
  if (typeof travelDays === 'string') {
    return travelDays;
  }
  return travelDays.join(', ');
};

function postRequest(path, data) {
  return request(path, {
    method: 'POST',
    body: JSON.stringify(data),
    headers: { 'Content-Type': 'application/json' },
  });
}

function putRequest(path, data) {
  return request(path, {
    method: 'PUT',
    body: JSON.stringify(data),
    headers: { 'Content-Type': 'application/json' },
  });
}

function getRequest(path: string, fetchOptions?: { signal?: AbortSignal }) {
  return request(path, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
    signal: fetchOptions?.signal,
  });
}

export interface PnrDiscovery {
  id: string;
  pnr_id: string;
  owner_pcc: string;
  contact_email: string | null;
  customer_name: string | null;
  contact_phone: string | null;
  airline_codes: string | null;
  brand_code: string | null;
  status: 'discovered' | 'reservation_created' | 'imported' | 'expired';
  ticketing_deadline: string | null;
  reservation_id: string | null;
  /** Pending while the imported order is unpaid, confirmed once it has been paid. */
  reservation_state?: string | null;
  order_id: string | null;
  customer_id: string | null;
  selling_price_cents: number | null;
  selling_currency: string | null;
  first_seen_at: string;
}

export async function getPnrDiscoveries(
  params: { page: number; pageSize: number; q: string; discoveredOnly: boolean },
  options?: { signal?: AbortSignal },
): Promise<{
  result: Array<PnrDiscovery>;
  pagination: { page: number; pageSize: number; total: number };
}> {
  const query = new URLSearchParams({
    page: String(params.page),
    pageSize: String(params.pageSize),
    q: params.q,
    discoveredOnly: String(params.discoveredOnly),
  });
  return getRequest(basePath() + `flights/pnr-discoveries?${query}`, options);
}

/** The parts of the live Sabre booking the PNR import reads. */
export interface PnrBooking {
  bookingJourney?: {
    legs?: Array<{
      price?: {
        total?: {
          total?: number;
          providerCost?: number;
          addonsCost?: number;
          markup?: number;
          currencyCode?: string;
        };
      };
      flights?: Array<{
        id?: string;
        marketingAirline?: { code?: string };
        flightNumber?: string;
        departureAirport?: string;
        arrivalAirport?: string;
        departingDate?: string;
        departingTime?: string;
        arrivalDate?: string;
        arrivalTime?: string;
        status?: string;
      }>;
    }>;
  };
  travelers?: Array<{
    id: string;
    title?: string;
    name?: string;
    lastName?: string;
    type?: string;
    email?: string;
    phone?: string;
  }>;
  /** Seats and extra baggage booked on the PNR, one per Sabre ancillary, told apart by their type. */
  ancillaries?: Array<
    | {
        type: 'SEAT';
        id: string;
        travelerId: string;
        flightId: string;
        flightNumber: string;
        seatNumber: string;
        commercialName?: string;
        price: { amount: number; currency: string };
      }
    | {
        type: 'BAGGAGE';
        id: string;
        travelerId: string;
        flightId: string;
        flightNumber: string;
        /** How many bags the ancillary covers; the price is the charge for all of them. */
        quantity: number;
        commercialName?: string;
        price: { amount: number; currency: string };
      }
  >;
  bookingConfiguration?: { brandCode?: string; region?: string };
  isTicketed?: boolean;
  ticketingDeadline?: string;
}

export async function getPnrDiscovery(pnr: string, options?: { signal?: AbortSignal }) {
  const response = await getRequest(basePath() + `flights/pnr-discoveries/${encodeURIComponent(pnr)}`, options);
  return response.result as { discovery: PnrDiscovery; booking: PnrBooking };
}

function patchRequest(path, data) {
  return request(path, {
    method: 'PATCH',
    body: JSON.stringify(data),
    headers: { 'Content-Type': 'application/json' },
  });
}

function deleteRequest(path) {
  return request(path, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
  });
}

export function retrievePNRBookingDetails(data) {
  return postRequest(basePath() + 'flights/retrievePNRBookingDetails', data);
}

export interface CreateFlightDealData {
  carrierCode: string;
  region: string;
  originAirportCode: string;
  destinationAirportCode: string;
  fareType: string;
  offerDurations: Array<{
    departureDate: string;
    arrivalDate: string;
  }>;
  perAdultPrice?: number;
  luxPlusPerAdultPrice?: number | null;
  searchText: string;
  salesStartDate: string;
  salesEndDate: string;
  dealName?: string;
  dealDescription?: string;
  heroImagePublicId?: string;
  categoryGroup: string;
  fareClass?: string;
  baggageIncluded?: boolean;
  fareName?: string;
  adultCount?: number;
  fareBasis?: string;
  discountLabel?: string;
  bookingClassCode: string;
  termsAndConditions: string;
  travelDays?: string | Array<string>;
  hierarchy?: number;
  airlineCampaign?: string;
  destinationCampaign?: string;
  departureAirportCampaign?: string;
}

export function createFlightDeal(data: CreateFlightDealData) {
  return postRequest(basePath() + 'flights/flight-deals/create', data);
}

export function updateFlightDeal(dealId: string, data: Partial<CreateFlightDealData>) {
  return patchRequest(basePath() + `flights/flight-deals/${dealId}/edit`, data);
}

export function deleteFlightDeal(dealId: string) {
  return deleteRequest(basePath() + `flights/flight-deals/${dealId}`);
}

export function retrieveAtolDetails(data) {
  return postRequest(basePath() + 'flights/atol/getAll', data);
}

export async function getCreditReservations(data) {
  return postRequest(basePath() + 'flights/credit-reservation/getAll', data);
}

export async function getCreditReservationDetails(id) {
  return getRequest(basePath() + `flights/credit-reservation/${id}/details`);
}

export async function getCreditReservationByPnrId(pnrId) {
  return getRequest(basePath() + `flights/credit-reservation/${pnrId}/by-previous-id`);
}

export async function updateCreditReservation(id, data) {
  return putRequest(basePath() + `flights/credit-reservation/${id}/update`, data);
}

export async function getRecordsDataByUrl(url, data) {
  return postRequest(basePath() + `flights/reports/${url}`, data);
}

export interface GetFlightDealsData {
  pageSize?: number;
  page?: number;
  attributes?: Array<string>;
  searchString?: string;
  filter?: {
    column: string;
    value: string;
  };
  filters?: Array<{
    column: string;
    value: string;
  }>;
  orderBy?: {
    column: string;
    sort: string;
  };
  activeOnly?: boolean;
}

export async function getFlightDeals(
  data: GetFlightDealsData,
): Promise<{ result: { count: number; rows: Array<App.FlightDeal> } }> {
  return postRequest(basePath() + 'flights/flight-deals/getAll' + draftPreviewSuffix(), data);
}

export async function getFlightDeal(dealId: string): Promise<{ result: App.FlightDeal }> {
  return getRequest(basePath() + `flights/flight-deals/${dealId}${draftPreviewSuffix()}`);
}

export type AirlineCarriersResponse =
  actions['/api/flights/airlines/get']['responses']['200']['content']['application/json'];

export function resolveFlightsAirlinesApiBrand(brand: string): string {
  if (brand === 'leagenthub_cvfr') {
    return 'leagenthub';
  }
  return brand;
}

export async function getAllAirlineCarriers(
  brand: string,
  searchCode?: string,
  options?: { signal?: AbortSignal },
): Promise<AirlineCarriersResponse> {
  const apiBrand = resolveFlightsAirlinesApiBrand(brand);
  const params = new URLSearchParams({ brand: apiBrand });
  if (searchCode) {
    params.append('searchCode', searchCode);
  }
  return getRequest(basePath() + `flights/airlines?${params.toString()}`, options);
}

type GetAllForceBundlesResponse = Result<Array<App.ForceBundle>>;
export async function getAllForceBundles(): Promise<GetAllForceBundlesResponse> {
  return getRequest(basePath() + 'flights/force-bundles');
}

type GetForceBundleResponse = Result<App.ForceBundle>;
export async function getForceBundle(id: string): Promise<GetForceBundleResponse> {
  return getRequest(basePath() + 'flights/force-bundles/' + id);
}

type ForceBundleAvailabilityRequest = {
  brand: string;
  forceBundleDealName: string;
  forceBundleId: string;
  origin: string;
  destination: string;
  region: string;
  startDate: string;
  endDate: string;
};
type ForceBundleAvailabilityResponse = Result<App.ForceBundle & { amountAvailable: number }>;
export async function getForceBundleAvailability(
  data: Partial<ForceBundleAvailabilityRequest>,
): Promise<ForceBundleAvailabilityResponse> {
  return postRequest(basePath() + 'flights/force-bundles/availability', {
    brand: 'luxuryescapes',
    ...data,
  });
}

export interface FDATable {
  name: string;
  displayName: string;
}

const CHARTER_FEEDER_PRICES_TABLE_NAME = 'CharterFeederPrices';
const CHARTER_FEEDER_PRICES_PAGE_LIMIT = 200;
const MAX_CHARTER_FEEDER_PRICES_PAGES = 50;

export interface CharterFeederPricesRow {
  Id: number;
  OriginAirportCode: string;
  DestinationAirportCode: string;
  Date: string;
  FeederOriginAirportCode: string;
  BasePrice: number;
  BufferPrice: number;
  ModifiedBy?: string | null;
  CreatedDate: string;
  ModifiedDate: string;
}

export type CharterFeederPriceCreateItem = {
  OriginAirportCode: string;
  DestinationAirportCode: string;
  Date: string;
  FeederOriginAirportCode: string;
  BasePrice: number;
  BufferPrice: number;
  ModifiedBy?: string;
};

export type CharterFeederPriceUpdateItem = {
  Id: number;
  changes: Partial<Omit<CharterFeederPriceCreateItem, 'ModifiedBy'>>;
};

export interface CharterFeederPricesBulkPayload {
  create?: Array<CharterFeederPriceCreateItem>;
  update?: Array<CharterFeederPriceUpdateItem>;
  deleteIds?: Array<number>;
}

export interface CharterFeederPricesBulkResult {
  created: Array<CharterFeederPricesRow>;
  updated: Array<CharterFeederPricesRow>;
  deletedIds: Array<number>;
  summary: {
    created: number;
    updated: number;
    deleted: number;
  };
}

export async function getFDATables(): Promise<{ result: Array<FDATable> }> {
  return getRequest(basePath() + 'flights/fda/table-names');
}

export async function getCharterFeederPrices(options?: {
  limit?: number;
  offset?: number;
  signal?: AbortSignal;
}): Promise<{ result: { count: number; rows: Array<CharterFeederPricesRow> } }> {
  const query = qs.stringify({
    limit: options?.limit ?? CHARTER_FEEDER_PRICES_PAGE_LIMIT,
    offset: options?.offset ?? 0,
  });

  return getRequest(basePath() + `flights/fda/${CHARTER_FEEDER_PRICES_TABLE_NAME}?${query}`, {
    signal: options?.signal,
  });
}

export async function fetchAllCharterFeederPrices(options?: {
  signal?: AbortSignal;
}): Promise<Array<CharterFeederPricesRow>> {
  const allRows: Array<CharterFeederPricesRow> = [];
  let offset = 0;
  let hasMore = true;
  let pageCount = 0;

  while (hasMore) {
    if (options?.signal?.aborted) {
      throw new DOMException('The operation was aborted.', 'AbortError');
    }

    if (pageCount >= MAX_CHARTER_FEEDER_PRICES_PAGES) {
      throw new Error(`Exceeded ${MAX_CHARTER_FEEDER_PRICES_PAGES} pages while loading Charter feeder prices.`);
    }

    const { result } = await getCharterFeederPrices({
      limit: CHARTER_FEEDER_PRICES_PAGE_LIMIT,
      offset,
      signal: options?.signal,
    });
    allRows.push(...result.rows);
    hasMore = allRows.length < result.count && result.rows.length === CHARTER_FEEDER_PRICES_PAGE_LIMIT;
    offset += CHARTER_FEEDER_PRICES_PAGE_LIMIT;
    pageCount += 1;
  }

  return allRows;
}

export async function bulkSaveCharterFeederPrices(
  payload: CharterFeederPricesBulkPayload,
): Promise<{ result: CharterFeederPricesBulkResult }> {
  return postRequest(basePath() + 'flights/fda/charter-feeder-prices/bulk', payload);
}

export async function getFDATableData(
  tableName: string,
  options?: {
    limit: number;
    offset: number;
    sortField?: string;
    sortDirection?: string;
    searchField?: string;
    searchValue?: string;
  },
): Promise<{ result: { count: number; rows: Array<Record<string, FDATableRecord>> } }> {
  let query = {};

  if (options) {
    query = {
      limit: options.limit,
      offset: options.limit * options.offset,
    };
  }

  if (options?.sortField && options?.sortDirection) {
    query = {
      ...query,
      orderBy: `${options.sortField}:${options.sortDirection.toUpperCase()}`,
    };
  }

  if (options?.searchField && options?.searchValue) {
    query = {
      ...query,
      filterBy: `{"${options.searchField}":"${options.searchValue}"}`,
    };
  }

  return getRequest(basePath() + `flights/fda/${tableName}?${qs.stringify(query)}`);
}

export type FDATableDataType = 'number' | 'string' | 'boolean' | 'date';

export interface FDATableSchema {
  name: string;
  displayName: string;
  dataType: FDATableDataType;
  allowNull: boolean;
  maxLength?: number;
}

interface FDATableSchemaResponse {
  attributes: Array<FDATableSchema>;
  searchSupported: boolean;
  sortableFields: Array<string>;
  searchableFields: Array<string>;
}

export async function getFDATableSchema(tableName: string): Promise<{ result: FDATableSchemaResponse }> {
  return getRequest(basePath() + `flights/fda/${tableName}/schema`);
}

type FDATableRecord = string | number | boolean;

export async function createFDATableRecord(
  tableName: string,
  data: Record<string, FDATableRecord>,
): Promise<{ result: Record<string, FDATableRecord> }> {
  return postRequest(basePath() + `flights/fda/${tableName}`, data);
}

export async function updateFDATableRecord(tableName: string, recordId: string, data: Record<string, FDATableRecord>) {
  return patchRequest(basePath() + `flights/fda/${tableName}/${recordId}`, data);
}

export async function deleteFDATableRecord(tableName: string, recordId: string) {
  return deleteRequest(basePath() + `flights/fda/${tableName}/${recordId}`);
}

type getCalendarSearchAdminPayload = definitions['CheapestPriceParams'];
export type getCalendarSearchAdminResponse =
  actions['/api/flights/calendar-search/admin/post']['responses']['200']['content']['application/json'];
export type CheapestPrice = definitions['CheapestPrice'];

export async function getCalendarSearchAdmin(
  payload: getCalendarSearchAdminPayload,
): Promise<getCalendarSearchAdminResponse> {
  return postRequest(basePath() + 'flights/calendar-search/admin', payload);
}

const FLIGHTS_CAMPAIGNS_BASE_API = basePath() + 'flights/campaigns';

export async function createFlightCampaign(
  data: Omit<App.FlightCampaign, 'updatedAt' | 'createdAt' | 'deals' | 'deleted' | 'id'>,
): Promise<{ status: number; result: App.FlightCampaign }> {
  return postRequest(FLIGHTS_CAMPAIGNS_BASE_API, data);
}

export type FlightCampaignType = 'airline' | 'destination' | 'departure_airport' | 'all';

export interface GetFlightCampaignsData {
  type?: FlightCampaignType;
  region?: string;
  active?: boolean;
  page?: number;
  pageSize?: number;
  filters?: Partial<
    Record<'id' | 'title' | 'type' | 'header' | 'region' | 'start_date' | 'end_date' | 'brand', string>
  >;
}

export async function getFlightCampaigns(
  filters: GetFlightCampaignsData = {},
  options?: { signal?: AbortSignal },
): Promise<{
  status: number;
  result: { rows: Array<App.FlightCampaign>; count: number };
}> {
  const { filters: campaignFilters, ...queryFilters } = filters;
  const query = qs.stringify(
    {
      ...queryFilters,
      ...(draftPreviewSuffix() ? { includeUnpublished: true } : {}),
      ...(campaignFilters?.id && { filterId: campaignFilters.id }),
      ...(campaignFilters?.title && { filterTitle: campaignFilters.title }),
      ...(campaignFilters?.type && { filterType: campaignFilters.type }),
      ...(campaignFilters?.header && { filterHeader: campaignFilters.header }),
      ...(campaignFilters?.region && { filterRegion: campaignFilters.region }),
      ...(campaignFilters?.start_date && { filterStartDate: campaignFilters.start_date }),
      ...(campaignFilters?.end_date && { filterEndDate: campaignFilters.end_date }),
      ...(campaignFilters?.brand && { filterBrand: campaignFilters.brand }),
    },
    { skipNulls: true },
  );
  return getRequest(`${FLIGHTS_CAMPAIGNS_BASE_API}?${query}`, options);
}

export async function deleteFlightCampaign(id: string): Promise<{
  status: number;
  result: null;
}> {
  return deleteRequest(`${FLIGHTS_CAMPAIGNS_BASE_API}/${id}`);
}

// Eligible admin reads include drafts; other readers keep their existing access.
export async function getFlightCampaign(id: string): Promise<{
  status: number;
  result: App.FlightCampaign;
}> {
  return getRequest(`${FLIGHTS_CAMPAIGNS_BASE_API}/${id}${draftPreviewSuffix()}`);
}

export async function updateFlightCampaign(
  id: string,
  data: Partial<Omit<App.FlightCampaign, 'updatedAt' | 'createdAt' | 'id'>>,
): Promise<{ status: number; result: App.FlightCampaign }> {
  return patchRequest(`${FLIGHTS_CAMPAIGNS_BASE_API}/${id}`, data);
}

export async function getFlightCampaignDeals(id: string): Promise<{
  status: number;
  result: { rows: Array<App.FlightDeal>; count: number };
}> {
  return getRequest(`${FLIGHTS_CAMPAIGNS_BASE_API}/${id}/deals${draftPreviewSuffix()}`);
}

export type BuilderFareClass = 'economy' | 'premium' | 'business' | 'first';
export type BuildPublishMode = 'now' | 'scheduled' | 'draft';

export interface BuildCampaignInput {
  type: FlightCampaignType;
  title: string;
  region: string;
  header: string;
  description: string;
  start_date: string;
  end_date: string;
  brand: string;
  hero_image_public_id: string;
  terms: string;
  airline_code?: string;
  price?: number;
  lux_plus_price?: number | null;
  fare_type?: App.FareType;
  fare_class?: App.FareClass;
  position?: number;
}

export interface BuildDealTemplate {
  carrierCode?: string;
  fareType: 'return' | 'oneWay';
  offerDurations: Array<{ departureDate: string; arrivalDate: string }>;
  categoryGroup: string;
  adultCount: number;
  discountLabel?: string;
  baggageIncluded: boolean;
  heroImagePublicId?: string;
  searchText?: string;
}

export interface BuildDealRowInput {
  carrierCode?: string;
  originAirportCode: string;
  destinationAirportCode: string;
  fareClass: BuilderFareClass;
  perAdultPrice: number;
  luxPlusPerAdultPrice: number | null;
  bookingClassCode: string;
  fareName?: string;
  fareBasis?: string;
  dealDescription?: string;
  hierarchy?: number;
  dealName?: string;
}

export interface BuildFlightCampaignBody {
  opportunityId?: string;
  campaignId?: string;
  campaign?: BuildCampaignInput;
  dealTemplate: BuildDealTemplate;
  rows: Array<BuildDealRowInput>;
  publish: BuildPublishMode;
  validateOnly?: boolean;
}

export type BuildIssueSeverity = 'error' | 'warning';

export interface BuildIssue {
  rowIndex: number | null;
  code: string;
  field?: string;
  message: string;
  severity: BuildIssueSeverity;
}

export interface BuildComputedRow extends BuildDealRowInput {
  carrierCode: string;
  dealName: string;
  hierarchy: number;
  salesStartDate?: string;
  salesEndDate?: string;
}

export interface BuildFlightCampaignResult {
  campaign?: App.FlightCampaign;
  deals?: Array<App.FlightDeal>;
  rows: Array<BuildComputedRow>;
  issues: Array<BuildIssue>;
  customerUrl?: string | null;
  status?: 'draft' | 'scheduled' | 'live' | 'ended';
}

// A 400 carries the blocking issues in the body, so it is returned rather than thrown
export function toPayloadIssues(errors: Array<{ path?: string; message?: string } | string>): Array<BuildIssue> {
  return errors.map((error) => {
    const path = typeof error === 'string' ? undefined : error.path;
    const message = typeof error === 'string' ? error : (error.message ?? 'is invalid');
    const rowMatch = path?.match(/^rows\[(\d+)\]\.(.+)$/);
    return {
      rowIndex: rowMatch ? Number(rowMatch[1]) : null,
      code: 'INVALID_PAYLOAD',
      field: rowMatch ? rowMatch[2] : path,
      message: path ? `${path} ${message}` : message,
      severity: 'error' as const,
    };
  });
}

export async function buildFlightCampaign(
  body: BuildFlightCampaignBody,
): Promise<{ status: number; message?: string; result: BuildFlightCampaignResult }> {
  try {
    return await postRequest(`${FLIGHTS_CAMPAIGNS_BASE_API}/build`, body);
  } catch (error) {
    const responseBody = (error as ApiError)?.responseBody as
      | {
          message?: string;
          result?: Partial<BuildFlightCampaignResult>;
          errors?: Array<{ path?: string; message?: string } | string>;
        }
      | undefined;
    if ((error as ApiError)?.status === 400 && Array.isArray(responseBody?.result?.issues)) {
      return {
        status: 400,
        message: responseBody.message,
        result: { rows: responseBody.result.rows ?? [], issues: responseBody.result.issues },
      };
    }
    // Request schema rejections carry field paths rather than issues; show them in the checks list
    if ((error as ApiError)?.status === 400 && Array.isArray(responseBody?.errors)) {
      return {
        status: 400,
        message: responseBody.message,
        result: { rows: [], issues: toPayloadIssues(responseBody.errors) },
      };
    }
    throw error;
  }
}

export async function publishFlightCampaign(
  id: string,
  at?: string,
): Promise<{ status: number; result: App.FlightCampaign }> {
  return postRequest(`${FLIGHTS_CAMPAIGNS_BASE_API}/${id}/publish`, at ? { at } : {});
}

export async function unpublishFlightCampaign(id: string): Promise<{ status: number; result: App.FlightCampaign }> {
  return postRequest(`${FLIGHTS_CAMPAIGNS_BASE_API}/${id}/unpublish`, {});
}

export const FLIGHTS_TRACES_BASE_API = basePath() + 'flights/traces';

export async function getFlightTraces(id: string): Promise<{ status: number; result: Array<App.FlightTraces> }> {
  return getRequest(`${FLIGHTS_TRACES_BASE_API}/list?reservationId=${id}`);
}

export async function downloadFlightTraces(reservationId: string): Promise<Response> {
  const params = new URLSearchParams({
    reservationId,
    operation: 'download',
  });

  return request(`${FLIGHTS_TRACES_BASE_API}/list?${params.toString()}`, {
    method: 'GET',
    returnResponse: true,
  });
}

export async function getFlightTrace(id: string): Promise<{ status: number; result: App.FlightTrace }> {
  return getRequest(`${FLIGHTS_TRACES_BASE_API}/fetch-trace?name=${id}`);
}

export async function getAllAirportCodes(brand: string) {
  return getRequest(basePath() + `flights/airports?brand=${brand}`);
}

export type AirportLocationSearchResponse =
  actions['/api/flights/locationSearch/get']['responses']['200']['content']['application/json'];

export async function searchAirportLocation(searchCode: string, brand: string): Promise<AirportLocationSearchResponse> {
  return getRequest(basePath() + `flights/locationSearch?${new URLSearchParams({ searchCode, brand })}`);
}

export async function patchFlightReservation(
  id: string,
  op: string,
): Promise<{
  status: number;
  result: App.FlightDetails;
}> {
  return patchRequest(`${basePath()}flights/reservation/${id}`, { op });
}

export type UpdateReservationTravellersPayload =
  actions['/api/flights/reservation/{id}/travellers/put']['requestBody']['content']['application/json'];
export type UpdateReservationTravellersResponse =
  actions['/api/flights/reservation/{id}/travellers/put']['responses']['200']['content']['application/json'];

export async function updateReservationTravellers(
  reservationId: string,
  payload: UpdateReservationTravellersPayload,
): Promise<UpdateReservationTravellersResponse> {
  return putRequest(`${basePath()}flights/reservation/${reservationId}/travellers`, payload);
}

export async function getFlightPenalties(reservationId: string): Promise<App.FlightPenalties> {
  const data = await getRequest(`${basePath()}flights/reservation/${reservationId}/penalties`);
  return data.result;
}

export type FindByPNRResponse =
  actions['/api/flights/reservation/findByPNR/get']['responses']['200']['content']['application/json'];

export async function findReservationByPNR(pnr: string): Promise<FindByPNRResponse['result']> {
  const data: FindByPNRResponse = await getRequest(
    `${basePath()}flights/reservation/findByPNR?pnr=${encodeURIComponent(pnr)}`,
  );
  return data.result;
}

export type UpdateReservationAddonsResponse =
  actions['/api/flights/addons/updateReservationAddons/post']['responses']['200']['content']['application/json'];
export type UpdateReservationAddonsPayload =
  actions['/api/flights/addons/updateReservationAddons/post']['requestBody']['content']['application/json'];

export async function updateReservationAddons(
  payload: UpdateReservationAddonsPayload,
): Promise<UpdateReservationAddonsResponse> {
  return postRequest(basePath() + 'flights/addons/updateReservationAddons', payload);
}

export interface ExchangeDatePriceCategory {
  fee: number;
  tax: number;
  total: number;
  providerCost: number;
}

export interface ExchangeDateFinancials {
  addonId: string;
  addonPrice: number;
  currency: string;
  leHandlingFee: number;
  airlineChangeFee: number;
  fareDifference: number;
  handlingFeeBreakdown: Record<string, { amount: number; currency: string }>;
  priceBreakdown: {
    adult: ExchangeDatePriceCategory;
    child: ExchangeDatePriceCategory;
    infant: ExchangeDatePriceCategory;
    total: ExchangeDatePriceCategory;
  };
  description: string;
}

export interface ReservationHistoryEntry {
  id: string;
  reservationId: string;
  changeType: 'EXCHANGE_DATE' | 'MANUAL_UPDATE';
  changedAt: string;
  triggerSource: string | null;
  triggeredByAddonId: string | null;
  triggeredByUserId: string | null;
  previousValues: Record<string, unknown>;
  createdAt: string;
  exchangeDateFinancials?: ExchangeDateFinancials;
}

export async function getReservationHistory(
  reservationId: string,
): Promise<{ status: number; result: Array<ReservationHistoryEntry> }> {
  return getRequest(`${basePath()}flights/reservation/${reservationId}/history`);
}
